/**
 * Ricalcolo delle permanenze in campo.
 *
 * Il registro eventi e l'unica fonte. Questa funzione ricostruisce da zero
 * chi era in campo e quando, a ogni modifica: mai aggiornamento incrementale.
 * Su qualche decina di eventi il costo e nullo ed elimina un'intera famiglia
 * di errori, perche non esiste uno stato parziale da mantenere coerente.
 *
 * Un giocatore puo avere piu permanenze nella stessa partita quando il
 * formato consente il rientro in campo, come nelle categorie con
 * sostituzioni libere.
 */

import type { Evento, Formato } from './formato.js';
import { fineRegolamentare, offsetPeriodo } from './formato.js';
import { convertiMinutoEffettivo, ordinaEventi } from './cronometro.js';

/**
 * Ingresso e uscita sono minuti di gioco effettivi (`convertiMinutoEffettivo`),
 * recuperi compresi: la durata e sempre uscita meno ingresso, anche a cavallo
 * di un intervallo.
 */
export interface Permanenza {
  giocatoreId: string;
  /** Progressivo della permanenza per quel giocatore, a partire da 1. */
  ordine: number;
  titolare: boolean;
  minutoIngresso: number;
  /** Null se la permanenza e ancora aperta al momento del calcolo. */
  minutoUscita: number | null;
  /** La permanenza si e chiusa per espulsione. */
  espulso: boolean;
}

export interface Incoerenza {
  eventoId: string;
  giocatoreId: string | null;
  messaggio: string;
}

export interface RisultatoRicalcolo {
  permanenze: Permanenza[];
  incoerenze: Incoerenza[];
  /** Minuti giocati per giocatore, somma delle permanenze. */
  minuti: Map<string, number>;
  espulsi: Set<string>;
}

interface Aperta {
  ordine: number;
  titolare: boolean;
  minutoIngresso: number;
}

/**
 * @param minutoFinale minuto effettivo a cui chiudere le permanenze ancora
 *   aperte: `minutoEffettivo` del cronometro, che per una partita conclusa e
 *   la fine dell'ultimo periodo e per una in corso il minuto corrente.
 */
export function ricalcolaPermanenze(
  f: Formato,
  eventi: readonly Evento[],
  minutoFinale: number,
): RisultatoRicalcolo {
  const permanenze: Permanenza[] = [];
  const incoerenze: Incoerenza[] = [];
  const aperte = new Map<string, Aperta>();
  const contatore = new Map<string, number>();
  const gialli = new Map<string, number>();
  const espulsi = new Set<string>();
  const effettivo = convertiMinutoEffettivo(f, eventi);

  const prossimoOrdine = (g: string): number => {
    const n = (contatore.get(g) ?? 0) + 1;
    contatore.set(g, n);
    return n;
  };

  const chiudi = (g: string, minuto: number, perEspulsione: boolean): void => {
    const a = aperte.get(g);
    if (!a) return;
    permanenze.push({
      giocatoreId: g,
      ordine: a.ordine,
      titolare: a.titolare,
      minutoIngresso: a.minutoIngresso,
      minutoUscita: Math.max(a.minutoIngresso, minuto),
      espulso: perEspulsione,
    });
    aperte.delete(g);
  };

  for (const e of ordinaEventi(eventi)) {
    const g = e.giocatoreId;

    switch (e.tipo) {
      case 'ingresso': {
        if (!g) break;
        if (aperte.has(g)) {
          incoerenze.push({
            eventoId: e.id,
            giocatoreId: g,
            messaggio: 'Ingresso di un giocatore gia in campo.',
          });
          break;
        }
        if (espulsi.has(g)) {
          incoerenze.push({
            eventoId: e.id,
            giocatoreId: g,
            messaggio: 'Ingresso di un giocatore espulso.',
          });
          break;
        }
        const ordine = prossimoOrdine(g);
        aperte.set(g, {
          ordine,
          // Titolare significa in campo dal fischio d'inizio: e la prima
          // permanenza, generata automaticamente all'avvio del timer.
          titolare: ordine === 1 && e.periodo === 1 && e.minuto === 0,
          minutoIngresso: effettivo(e.periodo, e.minuto),
        });
        break;
      }

      case 'uscita': {
        if (!g) break;
        if (!aperte.has(g)) {
          incoerenze.push({
            eventoId: e.id,
            giocatoreId: g,
            messaggio: 'Uscita di un giocatore che non risulta in campo.',
          });
          break;
        }
        chiudi(g, effettivo(e.periodo, e.minuto), false);
        break;
      }

      case 'cartellino_giallo': {
        if (!g) break;
        const n = (gialli.get(g) ?? 0) + 1;
        gialli.set(g, n);
        break;
      }

      case 'cartellino_rosso': {
        if (!g) break;
        if (!aperte.has(g)) {
          incoerenze.push({
            eventoId: e.id,
            giocatoreId: g,
            messaggio: 'Espulsione di un giocatore che non risulta in campo.',
          });
        }
        espulsi.add(g);
        chiudi(g, effettivo(e.periodo, e.minuto), true);
        break;
      }

      default:
        break;
    }
  }

  // Il doppio giallo produce un rosso registrato dall'applicazione. Se manca,
  // qualcuno ha cancellato l'espulsione lasciando i due gialli: va segnalato
  // ma non corretto d'ufficio, perche la fonte resta il registro eventi.
  for (const [g, n] of gialli) {
    if (n >= 2 && !espulsi.has(g)) {
      incoerenze.push({
        eventoId: '',
        giocatoreId: g,
        messaggio: 'Due cartellini gialli senza la corrispondente espulsione.',
      });
    }
  }

  for (const g of [...aperte.keys()]) chiudi(g, minutoFinale, false);

  permanenze.sort((a, b) => a.minutoIngresso - b.minutoIngresso || a.ordine - b.ordine);

  return { permanenze, incoerenze, minuti: minutiGiocati(permanenze, minutoFinale), espulsi };
}

/**
 * Minuti giocati per giocatore, somma delle permanenze. Serve anche alle
 * partecipazioni lette dal database: ingresso vuoto vale il fischio d'inizio,
 * uscita vuota il minuto finale.
 */
export function minutiGiocati(
  permanenze: readonly {
    giocatoreId: string;
    minutoIngresso: number | null;
    minutoUscita: number | null;
  }[],
  minutoFinale: number,
): Map<string, number> {
  const minuti = new Map<string, number>();
  for (const p of permanenze) {
    const durata = (p.minutoUscita ?? minutoFinale) - (p.minutoIngresso ?? 0);
    minuti.set(p.giocatoreId, (minuti.get(p.giocatoreId) ?? 0) + Math.max(0, durata));
  }
  return minuti;
}

// ---------------------------------------------------------------------------
// Partite generate da una distinta
// ---------------------------------------------------------------------------

/**
 * Una permanenza scritta come minuti cumulativi: e il modo in cui i dati demo
 * descrivono una partita, che diventa poi eventi come quelli del timer.
 */
export interface RigaDistinta {
  giocatoreId: string;
  titolare: boolean;
  /** Null: dal fischio d'inizio. */
  minutoIngresso: number | null;
  /** Null: fino alla fine della partita. */
  minutoUscita: number | null;
  espulso: boolean;
}

/**
 * Periodo di un minuto cumulativo scritto nella distinta. Il minuto di fine
 * di un tempo resta nel tempo che si chiude, come il cambio fatto
 * all'intervallo dal timer; oltre la fine regolamentare si e nel recupero
 * dell'ultimo tempo.
 */
export function periodoDelMinuto(minuto: number, f: Formato): number {
  for (let p = 1; p < f.nPeriodi; p++) {
    if (minuto <= fineRegolamentare(p, f)) return p;
  }
  return f.nPeriodi;
}

/**
 * Gli eventi che il timer avrebbe registrato per questa distinta: periodi alla
 * durata regolamentare, ingresso di ogni permanenza (i titolari al minuto 0
 * del primo periodo), uscita o espulsione quando c'e. Cosi la partita
 * registrata a mano ha un'unica fonte, gli eventi, come le altre.
 *
 * L'orario reale parte dalla data della partita e segue i minuti; dentro lo
 * stesso minuto l'inizio del periodo apre, le uscite precedono gli ingressi e
 * la fine del periodo chiude.
 */
export function eventiDaDistinta(
  f: Formato,
  righe: readonly RigaDistinta[],
  dataOra: string,
  nuovoId: () => string,
): Evento[] {
  const base = new Date(dataOra).getTime();
  const fine = fineRegolamentare(f.nPeriodi, f);
  const RANGO = { inizio_periodo: 0, uscita: 1, cartellino_rosso: 1, ingresso: 2, fine_periodo: 3 };
  const evento = (
    tipo: keyof typeof RANGO,
    giocatoreId: string | null,
    periodo: number,
    minuto: number,
  ): Evento => ({
    id: nuovoId(),
    tipo,
    giocatoreId,
    periodo,
    minuto,
    secondo: 0,
    orarioReale: new Date(base + minuto * 60_000 + RANGO[tipo]).toISOString(),
  });

  const eventi: Evento[] = [];
  for (let p = 1; p <= f.nPeriodi; p++) {
    eventi.push(evento('inizio_periodo', null, p, offsetPeriodo(p, f)));
    eventi.push(evento('fine_periodo', null, p, fineRegolamentare(p, f)));
  }
  for (const r of righe) {
    const ingresso = r.minutoIngresso ?? 0;
    eventi.push(evento('ingresso', r.giocatoreId, periodoDelMinuto(ingresso, f), ingresso));
    if (r.espulso) {
      const minuto = r.minutoUscita ?? fine;
      eventi.push(evento('cartellino_rosso', r.giocatoreId, periodoDelMinuto(minuto, f), minuto));
    } else if (r.minutoUscita != null) {
      eventi.push(
        evento('uscita', r.giocatoreId, periodoDelMinuto(r.minutoUscita, f), r.minutoUscita),
      );
    }
  }
  return ordinaEventi(eventi);
}

// ---------------------------------------------------------------------------
// Guardie del toggle campo/panchina
// ---------------------------------------------------------------------------

/**
 * Il secondo giallo di un giocatore non ancora espulso: l'evento a cui
 * l'applicazione aggancia il rosso del doppio giallo, allo stesso minuto.
 * Null con meno di due gialli o con un rosso gia registrato. Va chiamata sui
 * soli eventi attivi, e solo quando si registra un giallo: due gialli rimasti
 * senza rosso per una cancellazione voluta non si correggono d'ufficio.
 */
export function secondoGiallo(eventi: readonly Evento[], giocatoreId: string): Evento | null {
  const suoi = ordinaEventi(eventi).filter((e) => e.giocatoreId === giocatoreId);
  if (suoi.some((e) => e.tipo === 'cartellino_rosso')) return null;
  const gialli = suoi.filter((e) => e.tipo === 'cartellino_giallo');
  return gialli[1] ?? null;
}

export interface StatoCampo {
  inCampo: Set<string>;
  espulsi: Set<string>;
  giaEntrati: Set<string>;
  sostituzioniUsate: number;
  /** Giocatori consentiti in campo: scende a ogni espulsione. */
  massimoInCampo: number;
}

export function calcolaStatoCampo(f: Formato, eventi: readonly Evento[]): StatoCampo {
  const inCampo = new Set<string>();
  const espulsi = new Set<string>();
  const giaEntrati = new Set<string>();
  let sostituzioniUsate = 0;

  for (const e of ordinaEventi(eventi)) {
    const g = e.giocatoreId;
    if (!g) continue;
    if (e.tipo === 'ingresso') {
      inCampo.add(g);
      // Gli ingressi al fischio d'inizio sono i titolari, non sostituzioni.
      if (!(e.periodo === 1 && e.minuto === 0)) sostituzioniUsate++;
      giaEntrati.add(g);
    } else if (e.tipo === 'uscita') {
      inCampo.delete(g);
    } else if (e.tipo === 'cartellino_rosso') {
      inCampo.delete(g);
      espulsi.add(g);
    }
  }

  return {
    inCampo,
    espulsi,
    giaEntrati,
    sostituzioniUsate,
    massimoInCampo: f.giocatoriInCampo - espulsi.size,
  };
}

export type Esito = { ok: true } | { ok: false; messaggio: string };

export function verificaIngresso(f: Formato, stato: StatoCampo, giocatoreId: string): Esito {
  if (stato.inCampo.has(giocatoreId)) {
    return { ok: false, messaggio: 'Il giocatore risulta gia in campo.' };
  }
  if (stato.espulsi.has(giocatoreId)) {
    return { ok: false, messaggio: 'Un giocatore espulso non puo rientrare.' };
  }
  if (stato.inCampo.size >= stato.massimoInCampo) {
    // Dopo un cartellino rosso la squadra gioca in inferiorita numerica: il
    // massimo non e piu quello configurato.
    return {
      ok: false,
      messaggio: `In campo ci sono gia ${stato.massimoInCampo} giocatori.`,
    };
  }
  if (stato.giaEntrati.has(giocatoreId) && !f.rientroConsentito) {
    return { ok: false, messaggio: 'Il formato non consente il rientro in campo.' };
  }
  if (stato.sostituzioniUsate >= f.maxSostituzioni) {
    return {
      ok: false,
      messaggio: `Sostituzioni esaurite (${f.maxSostituzioni}).`,
    };
  }
  return { ok: true };
}

export function verificaUscita(stato: StatoCampo, giocatoreId: string): Esito {
  if (!stato.inCampo.has(giocatoreId)) {
    return { ok: false, messaggio: 'Il giocatore non risulta in campo.' };
  }
  return { ok: true };
}

/**
 * Il gioco non riparte con meno giocatori del consentito: ne all'inizio di
 * un periodo ne alla fine di una sospensione. A gioco in corso uno in meno
 * e normale, e il momento del cambio tra "Esce" ed "Entra"; alla ripartenza
 * e una dimenticanza, e falserebbe i minutaggi di tutti.
 * Se dalla panchina non puo entrare nessuno (panchina vuota, sostituzioni
 * esaurite, rientro vietato) la squadra gioca davvero in inferiorita, e
 * la decisione resta all'utente: `rimediabile` dice quale dei due casi e.
 */
export function verificaRipartenza(
  f: Formato,
  stato: StatoCampo,
  panchina: readonly string[],
  azione: 'iniziare il periodo' | 'riprendere il gioco' = 'iniziare il periodo',
): { ok: true } | { ok: false; rimediabile: boolean; messaggio: string } {
  const presenti = stato.inCampo.size;
  if (presenti >= stato.massimoInCampo) return { ok: true };

  const rimediabile = panchina.some((id) => verificaIngresso(f, stato, id).ok);
  const conteggio = `In campo ci sono ${presenti} giocatori su ${stato.massimoInCampo}`;
  return rimediabile
    ? {
        ok: false,
        rimediabile,
        messaggio: `${conteggio}: fai entrare chi manca prima di ${azione}.`,
      }
    : {
        ok: false,
        rimediabile,
        messaggio: `${conteggio} e dalla panchina non può entrare nessuno.`,
      };
}

/** Titolari attesi per poter avviare la partita. */
export function verificaFormazioneIniziale(f: Formato, titolari: number): Esito {
  return titolari === f.giocatoriInCampo
    ? { ok: true }
    : {
        ok: false,
        messaggio: `Servono esattamente ${f.giocatoriInCampo} titolari, ne sono stati scelti ${titolari}.`,
      };
}

/**
 * Eventi di ingresso dei titolari, generati all'avvio del timer.
 * Minuto 0 e periodo 1 sono la firma che li distingue dalle sostituzioni.
 */
export function eventiIngressoTitolari(
  titolari: readonly string[],
  orarioAvvio: Date,
  nuovoId: () => string,
): Evento[] {
  return titolari.map((giocatoreId) => ({
    id: nuovoId(),
    tipo: 'ingresso' as const,
    giocatoreId,
    periodo: 1,
    minuto: 0,
    secondo: 0,
    orarioReale: orarioAvvio.toISOString(),
  }));
}

export { offsetPeriodo };
